import React from "react";
import { Typography } from "@mui/material";

import {
  MarkdownTable,
  MarkdownThead,
  MarkdownTbody,
  MarkdownTr,
  MarkdownTh,
  MarkdownTd,
} from "../../../ui/markdownComponents";
import MarkdownRenderer from "../../../ui/MarkdownRenderer";
import { IMessageMetadata } from "../../../../types/chat";

export interface MarkdownDataTableColumn {
  value: string;
  label: string;
}

export interface ChatMarkdownDataTableProps<
  T extends Record<string, unknown> = Record<string, unknown>,
> {
  title?: string;
  columns: MarkdownDataTableColumn[];
  data: T[];
  /** Field used for React `key` on each row (defaults to first column `value`). */
  rowKeyField?: keyof T & string;
  metadata?: IMessageMetadata
}

const formatSum = (value: number) => {
  try {
    return (
      new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 2,
      }).format(value) || ""
    );
  } catch {
    return String(value || "");
  }
};

const isCurrencyField = (key: string): boolean => {
  return (
    key.endsWith("_amount") ||
    key === "recovery_opportunity" ||
    key === "contracted_rate"
  );
};

function formatCellPlainString(key: string, raw: unknown): string {
  if (raw === null || raw === undefined) {
    return "—";
  }

  if (Array.isArray(raw)) {
    return raw.length > 0 ? raw.join(", ") : "—";
  }

  switch (typeof raw) {
    case "boolean":
      return raw ? "Yes" : "No";
    case "number":
      return isCurrencyField(key) ? formatSum(raw) : String(raw);
    case "string":
      return raw;
    case "bigint":
      return String(raw);
    case "function":
    case "symbol":
      return "—";
    case "object":
      try {
        return JSON.stringify(raw);
      } catch {
        return "—";
      }
    default:
      return String(raw);
  }
}

const ChatMarkdownDataTable = <
  T extends Record<string, unknown> = Record<string, unknown>,
>({
  title,
  columns,
  data,
  rowKeyField,
  metadata,
}: ChatMarkdownDataTableProps<T>) => {
  if (!data.length || !columns.length) return null;

  const keyField = rowKeyField ?? (columns[0]?.value as keyof T & string);

  return (
    <div className="mt-4 w-full min-w-0">
      {title ? (
        <Typography
          variant="subtitle2"
          component="h3"
          sx={{ mb: 1, fontWeight: 700 }}
          className="text-chat-dark"
        >
          {title}
        </Typography>
      ) : null}

      <MarkdownTable>
        <MarkdownThead>
          <MarkdownTr>
            {columns.map((col) => (
              <MarkdownTh key={col.value}>{col.label}</MarkdownTh>
            ))}
          </MarkdownTr>
        </MarkdownThead>

        <MarkdownTbody>
          {data.map((row, index) => {
            const rowKey = keyField != null ? row[keyField] : undefined;
            const rowReactKey =
              rowKey !== null && rowKey !== undefined
                ? String(rowKey)
                : String(index);

            return (
              <MarkdownTr key={rowReactKey}>
                {columns.map((col) => {
                  const raw = row[col.value] as unknown;
                  const fullText = formatCellPlainString(col.value, raw);

                  return (
                    <MarkdownTd key={col.value}>
                      <MarkdownRenderer content={String(fullText)} metadata={metadata} />
                    </MarkdownTd>
                  );
                })}
              </MarkdownTr>
            );
          })}
        </MarkdownTbody>
      </MarkdownTable>
    </div>
  );
};

export default ChatMarkdownDataTable;
